import * as React from 'react';
import { Field, FieldArray, formValueSelector, reduxForm, InjectedFormProps } from 'redux-form';
import { flow, map, forEach, size, noop, keys } from 'lodash';
import { IBrandingState, II18nMessages } from '../../../../services/branding/models';
import { Button, Col, ControlLabel, Form, FormGroup, Row } from 'react-bootstrap';
import FieldFormControl from '../../../../components/FieldFormControl';
import SelectControl from '../../../../components/SelectControl';
import Destination from './components/Destination';
import Travellers from './components/Travellers';
import { connect } from 'react-redux';
import DatePickerControl from '../../../../components/DatePickerControl';
import { IChannel, IDestination, IProduct } from '../../../../data/api/models';
import { isAfter, isBefore, isFutureDate, isNotFalse, isRequired, isValidDate } from '../../../../util/validators';
import * as moment from 'moment';
import { showDestinations, showRegions } from '../../../../data/api/api';
import * as Commonmark from 'react-commonmark';
import { FaInfoCircle, FaCog } from 'react-icons/fa';
import { bindActionCreators } from 'redux';
import { actions as quoteActions, getInitValues } from '../../../../services/quote/reducer';
import { IQuoteActions } from '../../../../services/quote/models';
import VoucherCode from '../VoucherCode';
import { actions as authActions } from '../../../../services/auth/reducer';

interface ITripDetailsFormProps extends InjectedFormProps {
  destinations: IDestination[];
  i18n: II18nMessages;
  branding: IBrandingState;
  groupType: string;
  tripType: string;
  startDate: string;
  endDate: string;
  quotationDeclaration: boolean;
  maxTripDuration: string;
  region: string;
  servingCountry: string;
  sendingOrganisation: string;
  sendingField: string;
  residency: string;
  product: IProduct;
  destinationsValue: string[];
  channel: IChannel;

  renderDefinition(name: string): any;

  actions: {
    quote: IQuoteActions;
  };

  dispatch(action: void): any;
}

class TripDetailsForm extends React.Component<ITripDetailsFormProps, any> {
  public componentDidMount() {
    const { product, change, initialValues, dispatch, auth } = this.props;

    const data = getInitValues(product);

    forEach(data, (value, key) => {
      if (initialValues[key] === null) {
        change(key, value);
      }
    });

    const dateFromApi = (date) => {
      if (!date) {
        return '';
      }

      const parsedDate = moment(date).format('DD/MM/YYYY');

      return parsedDate !== 'Invalid Date' ? parsedDate : '';
    };

    if (auth && auth.isAuthenticated) {
      dispatch(change('travellers[0].title', auth.customer.title));
      dispatch(change('travellers[0].dob', dateFromApi(auth.customer.dob)));
      dispatch(change('travellers[0].firstName', auth.customer.firstName));
      dispatch(change('travellers[0].lastName', auth.customer.lastName));
      dispatch(change('travellers[0].email', auth.customer.email));
      dispatch(change('travellers[0].phone', auth.customer.phone1));
      dispatch(change('travellers[0].address.postcode', auth.customer.address.postcode));
      dispatch(change('travellers[0].address.line1', auth.customer.address.line1));
      dispatch(change('travellers[0].address.line2', auth.customer.address.line2));
      dispatch(change('travellers[0].address.town', auth.customer.address.town));
      dispatch(change('travellers[0].address.country', auth.customer.address.country));
    }
  }

  public componentWillReceiveProps(nextProps) {
    const {
      tripType, quotationDeclaration, startDate, endDate, actions, dispatch, change,
      maxTripDuration, destinationsValue, region, groupType, residency, branding, schemeId,
    } = this.props;

    if (schemeId && tripType !== nextProps.tripType) {
      dispatch(change('schemeId', ''));
      actions.quote.calculate(null, true);
    }

    if (tripType === 'annual' && branding.channel.key === 'procycle-b2c') {
      dispatch(change('maxTripDuration', 31));
    }
  }

  private transformLinks = (url) => {
    const documents = this.props.product.documents;
    const documentKeys = keys(documents);

    let documentName = '';
    documentKeys.forEach((documentKey) => {
      if (url === 'data_protection_notice' && documentKey.includes('Data Protection Notice')) {
        documentName = documentKey;
      }
    });

    if (documentName) {
      return documents[documentName];
    }

    return url;
  }

  private showVoucherCode = () => {
    const { branding } = this.props;

    return [
      'procycle-b2c',
      'salt-b2c',
      'salt-salvation-army-b2c',
      'salt-om-ls-b2c',
      'salt-ls-b2c',
      'salt-om-b2c',
      'salt-general-b2c',
    ].includes(branding.channel.key);
  }

  private getMaxStartDate = () => {
    const { tripType, branding } = this.props;
    const channels = {
      'procycle-b2c': { single: 300, annual: 30, longstay: 30 },
    };

    const defaultAnnualDays = 60;
    let maxStartDate = showRegions(tripType)
      ? moment().add(defaultAnnualDays, 'days')
      : null;

    const channelMaxStartDate = channels[branding.channel.key];
    if (channelMaxStartDate) {
      const days = channelMaxStartDate[tripType];
      maxStartDate = days ? moment().add(days, 'days') : null;
    }

    return maxStartDate;
  }

  public render() {
    const {
      i18n,
      submitting,
      tripType,
      groupType,
      handleSubmit,
      destinations,
      valid,
      startDate,
      endDate,
      product,
      destinationsValue,
      renderDefinition,
      submitFailed,
      branding,
    } = this.props;

    const regionsList = map(product.metadata.regions, (name, key) => ({
      label: name,
      value: key,
    }));

    let durationsList = [];

    if (product.metadata['trip_durations_' + tripType]) {
      durationsList = map(product.metadata['trip_durations_' + tripType], (name, key) => ({
        label: name,
        value: key,
      }));
    }

    const maxStartDate = this.getMaxStartDate();

    return (
      <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">

        {/* Intro */}

        <div className="section">
          <h3>{i18n.quote.tripDetails.quote}</h3>
          <p>
            It only takes a few minutes to get a quote for travel insurance.
            It is important that you answer these questions accurately.
            Click on the <FaInfoCircle/> for additional information which will help.
          </p>
        </div>

        <div className="section">
          <h4>{i18n.quote.tripDetails.basic}</h4>

          <Commonmark
            source={product.metadata.quotation_declaration}
            linkTarget="_blank"
            transformLinkUri={this.transformLinks}
          />

          <Row className="radio-horizontal">
            <Col sm={12}>
              <Field
                name="quotationDeclaration"
                type="checkbox"
                component={FieldFormControl}
                validate={[
                  isRequired,
                  isNotFalse,
                ]}
              >
                <div/>
                Please tick to confirm you agree to the above declaration
              </Field>
            </Col>
          </Row>

        </div>

        {/* Type Type */}

        {size(product.metadata.trip_type) > 1 && (
          <div className="section">
            {i18n.quote.tripDetails.basicInfo} {renderDefinition('trip_type')} <br/>

            <Row className="radio-horizontal margin-top">
              <Col sm={8}>
                {map(product.metadata.trip_type, (name, key) => {
                  return (
                    <Field
                      key={key}
                      name="tripType"
                      type="radio"
                      value={key}
                      component={FieldFormControl}
                    >
                      <div/>
                      {/* Div used for radio icon */}
                      {name}
                    </Field>
                  );
                })}
              </Col>
            </Row>
          </div>
        )}

        {/* Trip Dates */}

        <div className="section">
          {i18n.quote.tripDetails.dates} {renderDefinition('travel_dates')}
          <Row className="margin-top">
            <Col sm={6}>
              <FormGroup controlId="start-date">
                <Col componentClass={ControlLabel} sm={6}>
                  {i18n.quote.tripDetails.startDate}
                </Col>
                <Col sm={6}>
                  <Field
                    name="startDate"
                    minDate={moment()}
                    maxDate={maxStartDate}
                    component={DatePickerControl}
                    validate={[
                      isRequired,
                      isValidDate,
                      isFutureDate,
                      isBefore(endDate, 'Must be before end date'),
                      showRegions(tripType) ?
                        isBefore(
                          moment().add('60', 'days'),
                          'Must start within 60 days of current date.',
                        ) : noop,
                    ]}
                  />
                </Col>
              </FormGroup>
            </Col>

            {tripType === 'single' && (
              <Col sm={6}>
                <FormGroup controlId="end-date">
                  <Col componentClass={ControlLabel} sm={6}>
                    {i18n.quote.tripDetails.endDate}
                  </Col>
                  <Col sm={6}>
                    <Field
                      name="endDate"
                      minDate={startDate ? moment(startDate, 'DD/MM/YYYY') : moment()}
                      component={DatePickerControl}
                      validate={[
                        isRequired,
                        isValidDate,
                        isFutureDate,
                        isAfter(startDate, 'Must be after start date'),
                      ]}
                    />
                  </Col>
                </FormGroup>
              </Col>
            )}

            {showRegions(tripType) && branding.channel.key !== 'procycle-b2c' && (
              <Col sm={6}>
                <FormGroup controlId="max-trip-duration">
                  <Col componentClass={ControlLabel} sm={6}>
                    Max Trip Duration
                  </Col>
                  <Col sm={6}>
                    <Field
                      name="maxTripDuration"
                      component={SelectControl}
                      options={durationsList}
                      validate={[
                        isRequired,
                      ]}
                    />
                  </Col>
                </FormGroup>
              </Col>
            )}
          </Row>
        </div>

        {showDestinations(tripType) && (
          <div>
            <FieldArray
              name="destinations"
              i18n={i18n}
              destinations={destinations}
              component={Destination}
              destinationsValue={destinationsValue}
            />
          </div>
        )}

        {showRegions(tripType) && (
          <div className="section">
            {i18n.quote.tripDetails.destinations} {renderDefinition('region')}
            <Row className="margin-top">
              <Col sm={12}>
                <FormGroup controlId="destination">
                  <Col componentClass={ControlLabel} sm={3}>
                    {i18n.quote.tripDetails.region}
                  </Col>
                  <Col sm={9} className="destinations-inputs">
                    <Field
                      name="region"
                      type="text"
                      component={SelectControl}
                      options={regionsList}
                      validate={[
                        isRequired,
                      ]}
                    />
                  </Col>
                </FormGroup>
              </Col>
            </Row>
          </div>
        )}

        {(branding.channel.key === 'salt-om-ls-b2c' || branding.channel.key === 'salt-om-b2c') && (
          <div className="section">
            {i18n.quote.tripDetails.servingCountry} {renderDefinition('serving_country')}
            <Row className="margin-top">
              <Col sm={12}>
                <FormGroup controlId="serving-country">
                  <Col componentClass={ControlLabel} sm={3}>
                    {i18n.quote.tripDetails.servingCountry}
                  </Col>
                  <Col sm={6}>
                    <Field
                      name={`servingCountry`}
                      type="text"
                      component={FieldFormControl}
                      validate={[
                        isRequired,
                      ]}
                    />
                  </Col>
                </FormGroup>
              </Col>
            </Row>
          </div>
        )}

        {['salt-om-ls-b2c', 'salt-om-b2c', 'salt-ls-b2c', 'salt-b2c'].includes(branding.channel.key) && (
          <div className="section">
            {i18n.quote.tripDetails.sendingOrganisation} {renderDefinition('sending_organisation')}
            <Row className="margin-top">
              <Col sm={12}>
                <FormGroup controlId="sending-organisation">
                  <Col componentClass={ControlLabel} sm={3}>
                    {i18n.quote.tripDetails.sendingOrganisation}
                  </Col>
                  <Col sm={6}>
                    <Field
                      name={`sendingOrganisation`}
                      type="text"
                      component={FieldFormControl}
                      validate={[
                        isRequired,
                      ]}
                    />
                  </Col>
                </FormGroup>
              </Col>
            </Row>
          </div>
        )}

        {/* Group Type */}

        <div>
          <FieldArray
            name="travellers"
            i18n={i18n}
            product={product}
            component={Travellers}
            groupType={groupType}
            renderDefinition={renderDefinition}
          />
        </div>

        <div>
          <VoucherCode enabled={this.showVoucherCode()} {...this.props} />
        </div>

        <div className="btn-bar">
          <div className="pull-right">
            <Button
              bsStyle="primary"
              type="submit"
              disabled={submitting || (submitFailed && !valid)}
            >
              {submitting ? <FaCog className="fa-spin"/> : 'Next'}
            </Button>
          </div>
        </div>
      </Form>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state) => {
      const tripType = selector(state, 'tripType');
      const quotationDeclaration = selector(state, 'quotationDeclaration');
      const startDate = selector(state, 'startDate');
      const endDate = selector(state, 'endDate');
      const maxTripDuration = selector(state, 'maxTripDuration');
      const destinationsValue = selector(state, 'destinations');
      const region = selector(state, 'region');
      const groupType = selector(state, 'groupType');
      const residency = selector(state, 'residency');

      return {
        quote: state.quote,
        branding: state.branding,
        tripType,
        quotationDeclaration,
        startDate,
        endDate,
        maxTripDuration,
        destinationsValue,
        region,
        groupType,
        residency,
        auth: state.auth,

      };
    },
    (dispatch) => ({
      actions: {
        auth: bindActionCreators({ ...authActions }, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
      },
    }),
  ),
])(TripDetailsForm);
